<template>
  <div class="user-container">
    <!-- 搜索和操作区域 -->
    <div class="search-bar">
      <div class="search-left">
        <el-input v-model="searchForm.keyword" placeholder="搜索OpenID、备注、UnionID" clearable
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch" @clear="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-select v-model="searchForm.subscribe_scene" placeholder="关注渠道" clearable
          style="width: 150px; margin-right: 15px" @change="handleSearch">
          <el-option v-for="scene in subscribeScenes" :key="scene.value" :label="scene.label" :value="scene.value" />
        </el-select>

        <el-select v-model="searchForm.tag_id" placeholder="标签筛选" clearable style="width: 180px; margin-right: 15px"
          @change="handleSearch">
          <el-option v-for="tag in tags" :key="tag.tag_id" :label="`${tag.tag_name} (${tag.user_count})`"
            :value="tag.tag_id" />
        </el-select>

        <el-button type="primary" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>
        <!-- 在搜索按钮旁边添加清除搜索按钮 -->
        <el-button @click="handleClearSearch" :disabled="!isSearching">
          <el-icon>
            <Refresh />
          </el-icon>
          清除搜索
        </el-button>
      </div>

      <div class="search-right">
        <!-- 全选和导出按钮 -->
        <el-button style="margin-left: 10px;" type="warning" @click="selectAllCurrentPage"
          :disabled="filteredUsers.length === 0">
          <el-icon>
            <Select />
          </el-icon>
          全选本页
        </el-button>

        <el-dropdown @command="handleExportCommand">
          <el-button type="info" :loading="exporting" :disabled="users.length === 0">
            <el-icon>
              <Download />
            </el-icon>
            导出数据
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="openid">导出OpenID (TXT)</el-dropdown-item>
              <el-dropdown-item command="openid_csv">导出OpenID (CSV)</el-dropdown-item>
              <el-dropdown-item command="all_data">导出完整数据 (CSV)</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>


        <el-button type="success" @click="syncUsers" :loading="syncing">
          <el-icon>
            <Download />
          </el-icon>
          同步用户
        </el-button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="stats-bar">
      <el-row :gutter="16">
        <el-col :span="4">
          <el-statistic title="总用户数" :value="pagination.total" />
        </el-col>
        <el-col :span="4">
          <el-statistic title="已加载" :value="users.length" />
        </el-col>
        <el-col :span="4">
          <el-statistic title="已关注" :value="subscribedCount" />
        </el-col>
        <el-col :span="4">
          <el-statistic title="标签数" :value="tags.length" />
        </el-col>
        <el-col :span="4">
          <el-statistic title="本页数据" :value="filteredUsers.length" />
        </el-col>
        <el-col :span="4">
          <el-statistic title="已选择" :value="selectedUsers.length" />
        </el-col>
      </el-row>
    </div>
    <!-- 已选用户统计和操作 -->
    <div v-if="selectedUsers.length > 0" class="selected-users-bar">
      <el-alert type="info" :closable="false" show-icon>
        <template #title>
          <span>已选择 <strong>{{ selectedUsers.length }}</strong> 个用户</span>
          <el-button type="text" @click="clearSelection" style="margin-left: 10px;">
            清空选择
          </el-button>
          <el-button type="text" @click="confirmSelection" style="margin-left: 10px;">
            确认选择
          </el-button>
        </template>
      </el-alert>
    </div>

    <!-- 用户列表 -->
    <el-table :data="filteredUsers" stripe style="width: 100%; margin-top: 20px" v-loading="loading" height="600"
      @selection-change="handleSelectionChange" ref="userTableRef">
      <el-table-column type="selection" width="55" />
      <el-table-column prop="openid" label="OpenID" width="240">
        <template #default="{ row }">
          <div class="openid-cell">
            <div class="openid-text">{{ row.openid }}</div>
            <el-button type="text" size="small" @click="copyOpenId(row.openid)">
              <el-icon>
                <CopyDocument />
              </el-icon>
            </el-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="unionid" label="UnionID" width="220">
        <template #default="{ row }">
          <span v-if="row.unionid">{{ row.unionid }}</span>
          <el-tag v-else size="small" type="info">无</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="remark" label="备注" width="120">
        <template #default="{ row }">
          <span v-if="row.remark">{{ row.remark }}</span>
          <el-tag v-else size="small" type="info">无</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="tagid_list" label="标签" width="150">
        <template #default="{ row }">
          <div class="tags-cell">
            <el-tag v-for="tagId in (row.tagid_list || [])" :key="tagId" size="small" class="user-tag">
              {{ getTagName(tagId) }}
            </el-tag>
            <span v-if="!row.tagid_list || row.tagid_list.length === 0" class="no-tags">无标签</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="subscribe_time" label="关注时间" width="180">
        <template #default="{ row }">
          {{ formatTime(row.subscribe_time) }}
        </template>
      </el-table-column>

      <el-table-column prop="subscribe_scene" label="关注渠道" width="120">
        <template #default="{ row }">
          <el-tag :type="getSceneTagType(row.subscribe_scene)" size="small">
            {{ getSceneLabel(row.subscribe_scene) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="language" label="语言" width="80">
        <template #default="{ row }">
          <el-tag size="small">{{ row.language || 'zh_CN' }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="subscribe" label="状态" width="80">
        <template #default="{ row }">
          <el-tag :type="row.subscribe === 1 ? 'success' : 'danger'" size="small">
            {{ row.subscribe === 1 ? '已关注' : '未关注' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="操作" width="120" fixed="right">
        <template #default="scope">
          <el-button type="primary" size="small" @click="selectUser(scope.row)">
            <el-icon>
              <Select />
            </el-icon>
            选择
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination-container" v-if="pagination.total > 0">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
        :total="pagination.total" :page-sizes="[20, 50, 100, 200]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="handlePageChange" @current-change="handlePageChange" />
    </div>

    <!-- 空状态 -->
    <el-empty v-if="filteredUsers.length === 0 && !loading" description="暂无用户数据">
      <el-button type="primary" @click="syncUsers">同步用户</el-button>
    </el-empty>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import {
  Search, Download, CopyDocument, Select
} from '@element-plus/icons-vue'

export default {
  name: 'WechatUserView',
  components: { Search, Download, CopyDocument, Select },
  props: {
    accountId: {
      type: Number,
      required: true
    },
    storeId: {
      type: Number,
      required: true
    },
    userId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      syncing: false,
      exporting: false,
      users: [],
      selectedUsers: [],
      tags: [],
      searchForm: {
        keyword: '',
        subscribe_scene: '',
        tag_id: ''
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      isSearching: false, // 新增：标记搜索状态
      subscribeScenes: [
        { value: 'ADD_SCENE_SEARCH', label: '公众号搜索' },
        { value: 'ADD_SCENE_ACCOUNT_MIGRATION', label: '公众号迁移' },
        { value: 'ADD_SCENE_PROFILE_CARD', label: '名片分享' },
        { value: 'ADD_SCENE_QR_CODE', label: '扫描二维码' },
        { value: 'ADD_SCENE_PROFILE_LINK', label: '图文页内名称点击' },
        { value: 'ADD_SCENE_PROFILE_ITEM', label: '图文页右上角菜单' },
        { value: 'ADD_SCENE_PAID', label: '支付后关注' },
        { value: 'ADD_SCENE_WECHAT_ADVERTISEMENT', label: '微信广告' },
        { value: 'ADD_SCENE_REPRINT', label: '他人转载' },
        { value: 'ADD_SCENE_LIVESTREAM', label: '视频号直播' },
        { value: 'ADD_SCENE_CHANNELS', label: '视频号' },
        { value: 'ADD_SCENE_WXA', label: '小程序关注' },
        { value: 'ADD_SCENE_OTHERS', label: '其他' }
      ]
    }
  },
  computed: {
    // 移除前端筛选逻辑，直接返回 users
    filteredUsers() {
      return this.users;
    },

    subscribedCount() {
      return this.users.filter(user => user.subscribe === 1).length;
    }
  },
  mounted() {
    this.fetchUsers();
    this.fetchTags();
  },
  methods: {
    // 修改获取用户列表方法，支持搜索参数
    async fetchUsers() {
      this.loading = true;
      try {
        const requestData = {
          action: 'search_users', // 使用搜索接口
          account_id: this.accountId,
          store_id: this.storeId,
          user_id: this.userId, // PC端特有
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，添加到请求参数
        if (this.isSearching) {
          if (this.searchForm.keyword) {
            requestData.keyword = this.searchForm.keyword;
          }
          if (this.searchForm.subscribe_scene) {
            requestData.subscribe_scene = this.searchForm.subscribe_scene;
          }
          if (this.searchForm.tag_id) {
            requestData.tag_id = this.searchForm.tag_id;
          }
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/wechat_account.php`,
          requestData
        );

        if (data.success) {
          this.users = data.data.user_list || [];
          this.pagination.total = data.pagination?.total || 0;

          let message = `获取 ${this.users.length} 个用户`;
          if (this.isSearching) {
            message += ' (搜索模式)';
          }
          this.$message.success(message);
        } else {
          this.$message.error(data.error || '获取用户失败');
        }
      } catch (error) {
        this.$message.error('获取用户失败');
        console.error('获取用户错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 修改同步用户方法，重置搜索状态
    async syncUsers() {
      this.syncing = true;
      this.searchForm = {
        keyword: '',
        subscribe_scene: '',
        tag_id: ''
      };
      this.isSearching = false;
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/wechat_account.php`,
          {
            action: 'get_users',
            account_id: this.accountId,
            store_id: this.storeId,
            user_id: this.userId
          }
        );

        if (data.success) {
          this.users = data.data.user_list || [];
          this.pagination.total = data.pagination?.total || 0;
          this.pagination.page = 1;
          this.$message.success(`同步成功，共 ${this.users.length} 个用户`);
        } else {
          this.$message.error(data.error || '同步用户失败');
        }
      } catch (error) {
        this.$message.error('同步用户失败');
        console.error('同步用户错误:', error);
      } finally {
        this.syncing = false;
      }
    },
    // 获取标签列表
    async fetchTags() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/wechat_account.php`,
          {
            action: 'get_tags',
            account_id: this.accountId,
            store_id: this.storeId,
            user_id: this.userId
          }
        );

        if (data.success) {
          this.tags = data.data.tags || [];
        } else {
          console.error('获取标签失败:', data.error);
        }
      } catch (error) {
        console.error('获取标签错误:', error);
      }
    },

    // 修改搜索方法
    handleSearch() {
      this.pagination.page = 1;
      this.isSearching = true;
      this.fetchUsers();
    },

    // 添加清除搜索的方法（可选）
    handleClearSearch() {
      this.searchForm = {
        keyword: '',
        subscribe_scene: '',
        tag_id: ''
      };
      this.isSearching = false;
      this.pagination.page = 1;
      this.fetchUsers();
    },
    // 处理分页变化
    handlePageChange() {
      this.fetchUsers();
    },

    // 全选当前页面数据 - 完整修复版本
    selectAllCurrentPage() {
      if (this.$refs.userTableRef) {
        // 先清空当前选择
        this.$refs.userTableRef.clearSelection();
        // 选中所有行
        this.filteredUsers.forEach(row => {
          this.$refs.userTableRef.toggleRowSelection(row, true);
        });
        // 更新 selectedUsers 数据
        this.selectedUsers = [...this.filteredUsers];
        this.$message.success(`已全选本页 ${this.filteredUsers.length} 个用户`);
      } else {
        this.$message.error('表格引用未找到，请稍后重试');
      }
    },

    // 处理导出命令
    handleExportCommand(command) {
      switch (command) {
        case 'openid':
          this.exportOpenIds('txt');
          break;
        case 'openid_csv':
          this.exportOpenIds('csv');
          break;
        case 'all_data':
          this.exportAllData();
          break;
      }
    },

    // 导出OpenID - 修复编码问题
    async exportOpenIds(format = 'txt') {
      this.exporting = true;
      try {
        // 获取所有用户的OpenID
        const allOpenIds = await this.getAllOpenIds();

        if (allOpenIds.length === 0) {
          this.$message.warning('没有可导出的用户数据');
          return;
        }

        let fileContent, fileName, mimeType;

        if (format === 'csv') {
          // CSV格式：每行一个OpenID，没有表头，添加BOM确保Excel正确显示中文
          fileContent = '\uFEFF' + allOpenIds.join('\n');
          fileName = `openids_${this.accountId}_${new Date().getTime()}.csv`;
          mimeType = 'text/csv;charset=utf-8';
        } else {
          // TXT格式
          fileContent = allOpenIds.join('\n');
          fileName = `openids_${this.accountId}_${new Date().getTime()}.txt`;
          mimeType = 'text/plain;charset=utf-8';
        }

        // 创建Blob并下载
        this.downloadFile(fileContent, fileName, mimeType);
        this.$message.success(`成功导出 ${allOpenIds.length} 个OpenID`);
      } catch (error) {
        console.error('导出OpenID失败:', error);
        this.$message.error('导出失败');
      } finally {
        this.exporting = false;
      }
    },

    // 修改导出相关方法，传递搜索条件
    async getAllUsersData() {
      const allUsers = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = 100;

      try {
        while (hasMore) {
          const requestData = {
            action: 'search_users',
            account_id: this.accountId,
            store_id: this.storeId,
            user_id: this.userId,
            page: currentPage,
            page_size: pageSize
          };

          // 传递搜索条件
          if (this.isSearching) {
            if (this.searchForm.keyword) {
              requestData.keyword = this.searchForm.keyword;
            }
            if (this.searchForm.subscribe_scene) {
              requestData.subscribe_scene = this.searchForm.subscribe_scene;
            }
            if (this.searchForm.tag_id) {
              requestData.tag_id = this.searchForm.tag_id;
            }
          }

          const { data } = await axios.post(
            `${config.BASE_URL}/pc/api/wechat_account.php`,
            requestData
          );

          if (data.success && data.data.user_list && data.data.user_list.length > 0) {
            allUsers.push(...data.data.user_list);

            if (data.data.user_list.length < pageSize) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
          }
        }
      } catch (error) {
        console.error('获取用户数据失败:', error);
        throw new Error('获取用户数据失败');
      }

      return allUsers;
    },
    // 导出完整数据 - 修复编码问题
    async exportAllData() {
      this.exporting = true;
      try {
        // 获取所有用户的完整数据
        const allUsers = await this.getAllUsersData();

        if (allUsers.length === 0) {
          this.$message.warning('没有可导出的用户数据');
          return;
        }

        // 创建CSV内容（包含表头），添加BOM确保Excel正确显示中文
        const headers = ['OpenID', 'UnionID', '备注', '关注时间', '关注渠道', '语言', '状态', '标签'];
        let csvContent = '\uFEFF' + headers.join(',') + '\n';

        allUsers.forEach(user => {
          const row = [
            user.openid || '',
            user.unionid || '',
            user.remark || '',
            user.subscribe_time ? this.formatExportTime(user.subscribe_time) : '',
            user.subscribe_scene || '',
            user.language || '',
            user.subscribe === 1 ? '已关注' : '未关注',
            user.tagid_list ? user.tagid_list.join(';') : ''
          ];
          // 处理CSV特殊字符（逗号、引号等）
          const escapedRow = row.map(field => {
            if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
              return `"${field.replace(/"/g, '""')}"`;
            }
            return field;
          });
          csvContent += escapedRow.join(',') + '\n';
        });

        const fileName = `users_data_${this.accountId}_${new Date().getTime()}.csv`;
        this.downloadFile(csvContent, fileName, 'text/csv;charset=utf-8');
        this.$message.success(`成功导出 ${allUsers.length} 条用户数据`);
      } catch (error) {
        console.error('导出完整数据失败:', error);
        this.$message.error('导出失败');
      } finally {
        this.exporting = false;
      }
    },


    async getAllOpenIds() {
      const allOpenIds = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = 100;

      try {
        while (hasMore) {
          const requestData = {
            action: 'search_users',
            account_id: this.accountId,
            store_id: this.storeId,
            user_id: this.userId,
            page: currentPage,
            page_size: pageSize
          };

          // 传递搜索条件
          if (this.isSearching) {
            if (this.searchForm.keyword) {
              requestData.keyword = this.searchForm.keyword;
            }
            if (this.searchForm.subscribe_scene) {
              requestData.subscribe_scene = this.searchForm.subscribe_scene;
            }
            if (this.searchForm.tag_id) {
              requestData.tag_id = this.searchForm.tag_id;
            }
          }

          const { data } = await axios.post(
            `${config.BASE_URL}/pc/api/wechat_account.php`,
            requestData
          );

          if (data.success && data.data.user_list && data.data.user_list.length > 0) {
            const openids = data.data.user_list.map(user => user.openid);
            allOpenIds.push(...openids);

            if (data.data.user_list.length < pageSize) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
          }
        }
      } catch (error) {
        console.error('获取用户数据失败:', error);
        throw new Error('获取用户数据失败');
      }

      return allOpenIds;
    },
    // 下载文件 - 确保UTF-8编码
    downloadFile(content, fileName, mimeType = 'text/csv;charset=utf-8') {
      // 确保使用UTF-8编码的Blob
      const blob = new Blob([content], {
        type: mimeType
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 释放URL对象
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    },

    // 格式化导出时间
    formatExportTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      return date.toLocaleString('zh-CN');
    },

    // 处理选择变化
    handleSelectionChange(selection) {
      this.selectedUsers = selection;
    },

    // 清空选择
    clearSelection() {
      if (this.$refs.userTableRef) {
        this.$refs.userTableRef.clearSelection();
      }
      this.selectedUsers = [];
      this.$message.info('已清空选择');
    },

    // 确认选择并传递给父组件
    confirmSelection() {
      if (this.selectedUsers.length === 0) {
        this.$message.warning('请先选择用户');
        return;
      }

      // 触发选择事件，传递给父组件
      this.$emit('users-selected', this.selectedUsers);
      this.$message.success(`已选择 ${this.selectedUsers.length} 个用户`);
    },

    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '-';
      return new Date(timestamp * 1000).toLocaleString();
    },

    // 获取标签名称
    getTagName(tagId) {
      const tag = this.tags.find(t => t.tag_id == tagId);
      return tag ? tag.tag_name : `标签${tagId}`;
    },

    // 获取关注渠道标签类型
    getSceneTagType(scene) {
      const sceneTypes = {
        'ADD_SCENE_QR_CODE': 'success',
        'ADD_SCENE_SEARCH': 'primary',
        'ADD_SCENE_PROFILE_CARD': 'warning',
        'ADD_SCENE_PAID': 'danger'
      };
      return sceneTypes[scene] || 'info';
    },

    // 获取关注渠道显示文本
    getSceneLabel(scene) {
      const sceneObj = this.subscribeScenes.find(s => s.value === scene);
      return sceneObj ? sceneObj.label : scene;
    },

    // 复制OpenID
    copyOpenId(openid) {
      navigator.clipboard.writeText(openid).then(() => {
        this.$message.success('OpenID已复制到剪贴板');
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = openid;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.$message.success('OpenID已复制到剪贴板');
      });
    },

    // 选择用户
    selectUser(user) {
      this.$emit('user-selected', user);
      this.$message.success(`已选择用户: ${user.remark || user.openid}`);
    }
  }
}
</script>

<style scoped>
.user-container {
  padding: 20px;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.search-left {
  display: flex;
  align-items: center;
}

/* 添加按钮间距 */
.search-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 统计信息样式优化 */
.stats-bar {
  margin-bottom: 20px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stats-bar .el-col {
  text-align: center;
}

.openid-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.openid-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #666;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tags-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.user-tag {
  background: #f0f9ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.no-tags {
  color: #999;
  font-size: 12px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

/* 已选用户栏样式 */
.selected-users-bar {
  margin: 15px 0;
}

.selected-users-bar .el-alert {
  padding: 8px 16px;
}

.selected-users-bar .el-button {
  padding: 0 8px;
  height: auto;
}
</style>